<template>
  <!-- =====================================================================
       DanfeNfce.vue — DANFE NFC-e para PDV Desktop
       Layout idêntico ao PDF gerado pelo backend web.
  ====================================================================== -->
  <div v-if="visivel" class="danfe-overlay" @click.self="fechar">
    <div class="danfe-container" ref="danfeContainer">

      <!-- ── Barra de ações (não impressa) ────────────────────────────── -->
      <div class="danfe-toolbar no-print">
        <span class="danfe-toolbar__titulo">
          <i class="fas fa-file-invoice mr-2"></i>DANFE NFC-e
        </span>
        <div class="danfe-toolbar__acoes">
          <button
            class="danfe-btn danfe-btn--primary mr-2"
            :disabled="!podeImprimir"
            @click="imprimir"
          >
            <i class="fas fa-print mr-1"></i>Imprimir
          </button>
          <button class="danfe-btn danfe-btn--light" @click="fechar">
            <i class="fas fa-times"></i>
          </button>
        </div>
      </div>

      <!-- ── Loader ───────────────────────────────────────────────────── -->
      <div v-if="carregando" class="danfe-loader no-print">
        <span class="spinner-border mr-2"></span>Carregando dados...
      </div>

      <!-- ── Erro ─────────────────────────────────────────────────────── -->
      <div v-else-if="erro" class="danfe-erro">
        <i class="fas fa-exclamation-circle mr-2 text-danger"></i>{{ erro }}
      </div>

      <div v-else-if="htmlDanfePadrao" class="danfe-html-wrap">
        <iframe
          class="danfe-html-frame"
          :srcdoc="htmlDanfePadrao"
          title="Pré-visualização DANFE NFC-e"
        ></iframe>
      </div>

      <div v-else class="danfe-erro">
        Pré-visualização da DANFE não disponível para esta nota.
      </div>
    </div><!-- /danfe-container -->
  </div>
</template>

<script>
export default {
  name: "DanfeNfce",

  props: {
    visivel: { type: Boolean, default: false },
    uuid:    { type: String,  default: null  },
  },

  emits: ["fechar"],

  data() {
    return {
      carregando:    false,
      erro:          null,
      nfce:          null,
      emitente:      {},
      configuracao:  {},
      qrCodeUrl:     null,
      htmlDanfePadrao: "",
    };
  },

  computed: {
    itens() {
      return this.nfce?.itens ?? [];
    },
    pagamentos() {
      return this.nfce?.pagamentos ?? [];
    },
    totalDesconto() {
      return this.itens.reduce((s, i) => s + Number(i.desconto ?? 0), 0);
    },
    troco() {
      const pago   = this.pagamentos.reduce((s, p) => s + Number(p.valor ?? 0), 0);
      const total  = Number(this.nfce?.valorTotal ?? 0);
      const diff   = pago - total;
      return diff > 0.005 ? diff : 0;
    },
    statusFiscal() {
      return this.nfce?.status_fiscal ?? null;
    },
    eAutorizada() {
      return this.statusFiscal === "autorizada" && !!this.nfce?.protocolo;
    },
    eContingencia() {
      return !this.eAutorizada &&
             this.statusFiscal !== "rejeitada" &&
             !!this.nfce?.xml_assinado;
    },
    podeImprimir() {
      if (this.statusFiscal === "rejeitada") return false;
      return this.eAutorizada || !!this.nfce?.xml_assinado;
    },
    isHomolog() {
      return parseInt(this.configuracao?.ambiente ?? 2, 10) === 2;
    },
    logoUrl() {
      const p = this.emitente?.payload_json;
      if (!p) return null;
      try {
        const obj = typeof p === "string" ? JSON.parse(p) : p;
        return obj?.logo_url ?? obj?.logo ?? null;
      } catch { return null; }
    },
    urlConsulta() {
      return this._extrairUrlConsulta();
    },
  },

  watch: {
    visivel(val) {
      if (val && this.uuid) this.carregarDados();
      else if (!val) this.limpar();
    },
    uuid(val) {
      if (val && this.visivel) this.carregarDados();
    },
  },

  methods: {
    async carregarDados() {
      this.carregando = true;
      this.erro       = null;
      this.nfce       = null;
      this.emitente   = {};
      this.qrCodeUrl  = null;
      this.htmlDanfePadrao = "";

      try {
        const api = this.$electron;
        if (!api) throw new Error("Electron API não disponível.");

        const [dadosNfce, dadosEmitente, dadosConfig] = await Promise.all([
          api.nfce.getByUuid(this.uuid),
          api.emitente.buscarEmitente(),
          api.configuracaoNfce?.buscarConfiguracao?.() ?? Promise.resolve({}),
        ]);

        if (!dadosNfce) throw new Error("NFC-e não encontrada.");

        this.nfce         = dadosNfce;
        this.emitente     = dadosEmitente ?? {};
        this.configuracao = dadosConfig   ?? {};
        this.qrCodeUrl    = this._extrairQrCode(dadosNfce);

        try {
          const danfeRet = await api.nfce.gerarDanfeHtml(this.uuid);
          this.htmlDanfePadrao = (danfeRet && danfeRet.html) || "";
        } catch (_) {
          this.htmlDanfePadrao = "";
        }
      } catch (err) {
        this.erro = err.message || String(err);
      } finally {
        this.carregando = false;
      }
    },

    _extrairQrCode(nfce) {
      // Prioridade: xml_autorizado → xml_assinado → xml
      const xml = nfce.xml_autorizado || nfce.xml_assinado || nfce.xml || "";
      if (xml) {
        // Suporta <qrCode>URL</qrCode> e <qrCode><![CDATA[URL]]></qrCode>
        const m = xml.match(/<qrCode>(?:<!\[CDATA\[)?([\s\S]+?)(?:\]\]>)?<\/qrCode>/i);
        if (m?.[1]) {
          return m[1]
            .replace(/&amp;/g, "&")
            .replace(/&lt;/g, "<")
            .replace(/&gt;/g, ">")
            .replace(/&quot;/g, '"')
            .trim();
        }
      }
      return nfce.url_qr_code ?? null;
    },

    _extrairUrlConsulta() {
      if (!this.nfce) return null;
      const xml = this.nfce.xml_autorizado || this.nfce.xml_assinado || this.nfce.xml || "";
      if (xml) {
        const m = xml.match(/<urlChave>(?:<!\[CDATA\[)?([\s\S]+?)(?:\]\]>)?<\/urlChave>/i);
        if (m?.[1]) return m[1].replace(/&amp;/g, "&").trim();
      }
      // Fallback por UF
      const uf   = (this.emitente.uf || "").toUpperCase();
      const homolog = this.isHomolog;
      const urls = {
        SC: homolog ? "https://hom.sat.sef.sc.gov.br/nfce/consulta" : "https://sat.sef.sc.gov.br/nfce/consulta",
        SP: homolog ? "https://homologacao.nfce.fazenda.sp.gov.br/NFCeConsultaPublica" : "https://www.nfce.fazenda.sp.gov.br/NFCeConsultaPublica",
        PR: "https://www.fazenda.pr.gov.br/nfce/",
        RS: "https://www.sefaz.rs.gov.br/NFCE/",
        MG: "https://nfce.fazenda.mg.gov.br/",
        RJ: "https://www.nfce.fazenda.rj.gov.br/",
        GO: "https://nfce.sefaz.go.gov.br/",
        BA: "https://www.sefaz.ba.gov.br/nfce/",
        PE: "https://nfce.sefaz.pe.gov.br/",
        CE: "https://nfce.sefaz.ce.gov.br/",
        MT: "https://www.sefaz.mt.gov.br/-nfce/",
        MS: "https://www.dfe.ms.gov.br/",
        PA: "https://www.sefa.pa.gov.br/nfce/",
        AM: "https://sistemas.sefaz.am.gov.br/nfce/",
        ES: "https://app.sefaz.es.gov.br/ConsultaNFCe",
        DF: "https://dec.fazenda.df.gov.br/ConsultarNFCe.aspx",
        RN: "https://nfce.set.rn.gov.br/",
        PB: "https://www.receita.pb.gov.br/nfce/",
        AL: "https://www.sefaz.al.gov.br/nfce/",
        PI: "https://nfce.sefaz.pi.gov.br/",
        SE: "https://nfce.se.gov.br/",
        MA: "https://www.sefaz.ma.gov.br/nfce/",
        TO: "https://sefaz.to.gov.br/nfce/",
        RO: "https://www.sefin.ro.gov.br/nfce/",
        RR: "https://portal.sefaz.rr.gov.br/nfce/",
        AP: "https://www.sefaz.ap.gov.br/nfce/",
        AC: "https://sat.sefa.ac.gov.br/p/nfce/",
      };
      return urls[uf] ?? null;
    },

    async imprimir() {
      if (!this.podeImprimir || !this.uuid) return;
      try {
        const api = this.$electron;
        if (!api?.printer?.imprimirDanfeNfce) {
          throw new Error("Serviço de impressão não disponível.");
        }
        await api.printer.imprimirDanfeNfce({ uuid_venda_terminal: this.uuid });
      } catch (e) {
        this.erro = e?.message || "Erro ao enviar DANFE para impressão.";
      }
    },

    fechar() {
      this.$emit("fechar");
    },

    limpar() {
      this.nfce         = null;
      this.emitente     = {};
      this.configuracao = {};
      this.qrCodeUrl    = null;
      this.htmlDanfePadrao = "";
      this.erro         = null;
    },

    fmtN(v) {
      return Number(v ?? 0).toLocaleString("pt-BR", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },

    formatQtd(v) {
      const n = Number(v ?? 0);
      return n % 1 === 0
        ? String(n)
        : n.toLocaleString("pt-BR", { minimumFractionDigits: 3, maximumFractionDigits: 3 });
    },

    formatarDH(dt) {
      if (!dt) return "—";
      try {
        return new Date(dt).toLocaleString("pt-BR", {
          day: "2-digit", month: "2-digit", year: "numeric",
          hour: "2-digit", minute: "2-digit", second: "2-digit",
        });
      } catch { return String(dt); }
    },

    formatCnpj(v) {
      if (!v) return "—";
      const s = String(v).replace(/\D/g, "");
      if (s.length !== 14) return v;
      return s.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, "$1.$2.$3/$4-$5");
    },

    formatCpfCnpj(v) {
      if (!v) return "—";
      const s = String(v).replace(/\D/g, "");
      if (s.length === 11) return s.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})$/, "$1.$2.$3-$4");
      if (s.length === 14) return s.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, "$1.$2.$3/$4-$5");
      return v;
    },

    formatChave(v) {
      if (!v) return "—";
      return String(v).replace(/(.{4})(?=.)/g, "$1 ").trim();
    },
  },
};
</script>

<style scoped>
/* ── Overlay ──────────────────────────────────────────────────────────────── */
.danfe-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.55);
  z-index: 2000;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  overflow-y: auto;
  padding: 2rem 1rem;
}

/* ── Container ────────────────────────────────────────────────────────────── */
.danfe-container {
  background: #fff;
  border-radius: 6px;
  box-shadow: 0 4px 24px rgba(0,0,0,0.18);
  width: 100%;
  max-width: 480px;
  overflow: hidden;
}

.danfe-html-wrap {
  background: #fff;
}

.danfe-html-frame {
  width: 100%;
  height: 78vh;
  border: 0;
  background: #fff;
}

/* ── Toolbar ──────────────────────────────────────────────────────────────── */
.danfe-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.65rem 1rem;
  background: var(--primary-color, #ee7361);
  color: #fff;
}
.danfe-toolbar__titulo { font-weight: 700; font-size: 0.9rem; }
.danfe-toolbar__acoes  { display: flex; align-items: center; gap: 0.25rem; }

/* ── Botões ───────────────────────────────────────────────────────────────── */
.danfe-btn {
  padding: 0.3rem 0.75rem;
  border-radius: 4px;
  font-size: 0.8rem;
  cursor: pointer;
  border: 1px solid transparent;
}
.danfe-btn:disabled { opacity: 0.45; cursor: not-allowed; }
.danfe-btn--primary  { background: var(--primary-color, #ee7361); color: #fff; border-color: var(--primary-color, #ee7361); }
.danfe-btn--primary:hover:not(:disabled) { background: #e05a47; }
.danfe-btn--light { background: transparent; color: #adb5bd; font-size: 1rem; padding: 0.3rem 0.5rem; }
.danfe-btn--light:hover { color: #fff; }

/* ── Loader / Erro ────────────────────────────────────────────────────────── */
.danfe-loader, .danfe-erro { padding: 2rem; text-align: center; color: #6c757d; font-size: 0.9rem; }

/* ── Corpo ────────────────────────────────────────────────────────────────── */
.danfe-body {
  padding: 1rem;
  font-size: 0.78rem;
  font-family: 'Courier New', Courier, monospace;
  color: #1a1a1a;
  line-height: 1.5;
}

/* ── Banner homologação ───────────────────────────────────────────────────── */
.danfe-homolog-banner {
  background: #ffec00;
  color: #000;
  text-align: center;
  font-weight: 700;
  font-size: 1rem;
  padding: 0.5rem;
  margin-bottom: 0.75rem;
  border: 2px solid #ccc;
  line-height: 1.4;
}

/* ── Cabeçalho emitente ───────────────────────────────────────────────────── */
.danfe-header {
  text-align: center;
  margin-bottom: 0.5rem;
}
.danfe-logo {
  display: block;
  margin: 0 auto 0.35rem;
  width: 72px;
  height: auto;
  object-fit: contain;
}
.danfe-emitente-nome {
  font-size: 0.95rem;
  font-weight: 700;
  text-transform: uppercase;
}
.danfe-emitente-detalhe { font-size: 0.72rem; color: #444; }

/* ── Separador ────────────────────────────────────────────────────────────── */
.danfe-sep {
  border: none;
  border-top: 1px dashed #aaa;
  margin: 0.5rem 0;
}

/* ── Título ───────────────────────────────────────────────────────────────── */
.danfe-titulo-doc {
  text-align: center;
  font-size: 0.75rem;
  font-weight: 600;
}
.danfe-subtitulo-doc {
  text-align: center;
  font-size: 0.7rem;
  color: #555;
}

/* ── Tabela de itens ──────────────────────────────────────────────────────── */
.danfe-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.72rem;
}
.danfe-table th {
  font-size: 0.65rem;
  text-transform: uppercase;
  color: #666;
  border-bottom: 1px solid #ccc;
  padding: 0.15rem 0.2rem;
  font-weight: 700;
}
.danfe-table td {
  padding: 0.15rem 0.2rem;
  vertical-align: top;
  border-bottom: 1px dotted #eee;
}
.danfe-table tr:last-child td { border-bottom: none; }
.t-cod  { width: 8%;  }
.t-desc { width: 42%; }
.t-un   { width: 8%;  text-align: center; }
.t-num  { width: 14%; text-align: right; }

/* ── Totais ───────────────────────────────────────────────────────────────── */
.danfe-totais-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.75rem;
}
.danfe-totais-table td, .danfe-totais-table th {
  padding: 0.1rem 0.2rem;
}
.t-r { text-align: right; }
.danfe-total-final td, .danfe-total-final th {
  font-size: 0.88rem;
  padding-top: 0.3rem;
  border-top: 1px solid #aaa;
}

/* ── Pagamentos ───────────────────────────────────────────────────────────── */
.danfe-pag-header th {
  font-size: 0.65rem;
  text-transform: uppercase;
  color: #333;
  border-bottom: 1px solid #ccc;
  padding: 0.15rem 0.2rem;
  font-weight: 700;
}

/* ── Consulta / Chave ─────────────────────────────────────────────────────── */
.danfe-consulta { text-align: center; margin: 0.3rem 0; }
.danfe-consulta-label {
  font-size: 0.7rem;
  font-weight: 700;
}
.danfe-consulta-url {
  font-size: 0.68rem;
  color: #444;
  word-break: break-all;
}
.danfe-chave {
  text-align: center;
  font-size: 0.65rem;
  word-break: break-all;
  letter-spacing: 0.04em;
  color: #333;
  margin: 0.2rem 0;
}

/* ── Consumidor ───────────────────────────────────────────────────────────── */
.danfe-consumidor {
  text-align: center;
  font-size: 0.72rem;
  margin: 0.15rem 0;
}

/* ── Identificação da nota ────────────────────────────────────────────────── */
.danfe-nota-id {
  text-align: center;
  font-size: 0.78rem;
  margin: 0.2rem 0;
}

/* ── Protocolo ────────────────────────────────────────────────────────────── */
.danfe-protocolo {
  text-align: center;
  font-size: 0.72rem;
  color: #333;
  margin: 0.1rem 0;
}

/* ── Contingência ─────────────────────────────────────────────────────────── */
.danfe-contingencia {
  text-align: center;
  font-size: 0.7rem;
  color: #664d03;
  background: #fff3cd;
  padding: 0.25rem 0.5rem;
  border-radius: 3px;
  margin: 0.3rem 0;
}

/* ── QR Code ──────────────────────────────────────────────────────────────── */
.danfe-qrcode-wrap {
  display: flex;
  justify-content: center;
  padding: 0.5rem 0;
}

/* ── Tributos ─────────────────────────────────────────────────────────────── */
.danfe-tributos {
  text-align: center;
  font-size: 0.68rem;
  color: #555;
  margin: 0.2rem 0;
}

/* ── Rodapé ───────────────────────────────────────────────────────────────── */
.danfe-rodape {
  text-align: right;
  font-size: 0.65rem;
  font-style: italic;
  color: #888;
  margin-top: 0.3rem;
}
</style>

<!-- ── CSS global de impressão ──────────────────────────────────────────── -->
<style>
@media print {
  body.danfe-printing * { visibility: hidden; }
  body.danfe-printing #danfe-print-area,
  body.danfe-printing #danfe-print-area * { visibility: visible; }
  body.danfe-printing #danfe-print-area {
    position: fixed;
    top: 0; left: 0;
    width: 80mm;
    padding: 3mm 4mm;
    background: #fff;
    font-family: 'Courier New', Courier, monospace;
    font-size: 8pt;
    color: #000;
  }
  .danfe-section, .danfe-totais-table, .danfe-qrcode-wrap, .danfe-chave {
    page-break-inside: avoid;
  }
}
</style>
